<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import ConversationApi from 'dashboard/api/inbox/conversation';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Asks a WhatsApp contact known only by its business scoped user id to share its phone number.
// The backend decides availability for the open conversation only, so list rendering stays cheap.
const props = defineProps({
  conversationId: { type: Number, required: true },
});

const emit = defineEmits(['requestTemplate']);

const store = useStore();
const { t } = useI18n();

const currentChat = useMapGetter('getSelectedChat');

const capability = ref({});
const isRequesting = ref(false);

const contactInfoRequests = computed(() =>
  (currentChat.value?.messages || [])
    .map(message => ({
      id: message.id,
      status: message.status,
      ...(message.content_attributes?.whatsapp_contact_info || {}),
    }))
    .filter(request => request.type === 'request')
);

const hasPendingRequest = computed(() =>
  contactInfoRequests.value.some(
    request => request.state === 'pending' && request.status !== 'failed'
  )
);

// Changes when a request is sent, answered or fails, so availability is loaded again.
const requestStateKey = computed(() =>
  contactInfoRequests.value
    .map(request => `${request.id}:${request.state}:${request.status}`)
    .join('|')
);

const fetchCapability = async () => {
  const conversationId = props.conversationId;
  try {
    const { data } =
      await ConversationApi.getContactInfoRequest(conversationId);
    if (conversationId === props.conversationId) capability.value = data;
  } catch {
    if (conversationId === props.conversationId) capability.value = {};
  }
};

watch(
  () => props.conversationId,
  () => {
    capability.value = {};
    fetchCapability();
  },
  { immediate: true }
);
watch(requestStateKey, fetchCapability);

const showButton = computed(
  () =>
    capability.value.available ||
    capability.value.reason === 'pending_request' ||
    hasPendingRequest.value
);

const isDisabled = computed(
  () =>
    isRequesting.value ||
    hasPendingRequest.value ||
    capability.value.reason === 'pending_request'
);

const tooltip = computed(() =>
  isDisabled.value && !isRequesting.value
    ? t('CONVERSATION.REQUEST_CONTACT_INFO.PENDING_ACTION')
    : t('CONVERSATION.REQUEST_CONTACT_INFO.ACTION')
);

const requestContactInfo = async () => {
  if (capability.value.delivery_mode === 'template') {
    emit('requestTemplate');
    return;
  }

  isRequesting.value = true;
  try {
    await store.dispatch('requestContactInfo', props.conversationId);
  } catch (error) {
    useAlert(error?.response?.data?.error || t('CONVERSATION.MESSAGE_ERROR'));
  } finally {
    isRequesting.value = false;
  }
};
</script>

<template>
  <NextButton
    v-if="showButton"
    v-tooltip.top-end="tooltip"
    icon="i-ph-address-book"
    slate
    faded
    sm
    :disabled="isDisabled"
    :is-loading="isRequesting"
    @click="requestContactInfo"
  />
  <template v-else />
</template>
